import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, OnChanges, SimpleChanges, OnInit, EventEmitter, Output, TemplateRef } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { get } from 'lodash';
import { camelize, titleize, underscore } from 'inflection';
import { NameValueProperty, PropertyFieldDefinition, PropertyFieldDefinitionInput } from '@nuso/common/types';
import { formatBandwidth, formatDecimal } from '@nuso/common/util-number';

const UNICODE_CHECKMARK = '\u2714';
const UNICODE_DISABLED = '\u2718'

function normalizePropertyFieldDefinitionInput(def: PropertyFieldDefinitionInput): PropertyFieldDefinition {
  const prop = def.prop || def.name && camelize(def.name);
  const name = def.name || def.prop && propToTitle(def.prop);
  const defaultValue = def.defaultValue === undefined ? null : def.defaultValue;

  return {
    prop,
    name,
    description: def.description,
    defaultValue,
    action: def.action,
    action2: def.action2,
    action3: def.action3,
    style: def.style,
    format: def.format,
    formatArgs: def.formatArgs,
  } as PropertyFieldDefinition;
}

function propToTitle(s: string) {
  return titleize(underscore(s.replace('.', ' ')));
}


@Component({
    selector: 'nuso-properties-table',
    templateUrl: './properties-table.component.html',
    styleUrls: ['./properties-table.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PropertiesTableComponent implements OnInit, OnChanges {

  nameValueColumns: string[];
  dataSource = new MatTableDataSource();

  _fieldDefinitions: PropertyFieldDefinition[];
  _data: any;

  // Either set a property of name value arrays
  @Input()
  set properties(p: NameValueProperty[]) {
    this.dataSource.data = p;
  }

  /** Allows defining custom value templates per field */
  @Input() valueTemplates: Record<string, TemplateRef<any>> = {};

  // Or, pass in an array of what fields you want displayed and how,
  // along with the raw object
  @Input()
  set fields(input: (string | PropertyFieldDefinitionInput)[]) {
    this._fieldDefinitions = (input || []).map(d => {
      return typeof d === 'string'
        ? { prop: d, name: propToTitle(d), defaultValue: null } as unknown as PropertyFieldDefinition
        : normalizePropertyFieldDefinitionInput(d)
    });

  }

  @Input() data: any;

  @Input() showActions = false;

  @Input() isDense = true;
  @Input() trueValue = UNICODE_CHECKMARK;
  @Input() falseValue = UNICODE_DISABLED;

  @Output() actionClicked = new EventEmitter<NameValueProperty>();
  @Output() action2Clicked = new EventEmitter<NameValueProperty>();
  @Output() action3Clicked = new EventEmitter<NameValueProperty>();


  constructor() { }

  ngOnInit() {
    this.nameValueColumns = this.showActions ? ['name', 'value', 'actions'] : ['name', 'value'];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fields'] || changes['data']) {
      this.updateProperties();
    }
  }

  updateProperties() {
    this.dataSource.data = this._fieldDefinitions.map((def) => {
      const { prop, name, defaultValue, format, formatArgs } = def;
      const rawValue = get(this.data, prop, null);
      const normalizedValue = this.normalizeValue(rawValue, defaultValue);
      const formattedValue = format ? this.formatValue(normalizedValue, format, formatArgs) : normalizedValue;

      return {
        name,
        value: formattedValue,
        def
      }
    });
  }

  private normalizeValue(value: any, defaultValue: any) {
    if (value === null ) {
      return defaultValue;
    }

    if (typeof value === 'boolean') {
      return value ? this.trueValue : this.falseValue;
    }

    return value;
  }

  private formatValue(value: any, format: string, formatArgs?: string): string {
    if (value === null || value === undefined) {
      return '';
    }

    switch (format) {
      case 'percent':
      case 'percentage':
        return `${(Number(value) * 100).toFixed(1)}%`;

      case 'mbps':
      case 'bandwidth':
        return formatBandwidth(Number(value));

      case 'decimal':
        return formatDecimal(Number(value), formatArgs);

      case 'number':
        return Number(value).toString();

      default:
        return value.toString();
    }
  }



}
